// Kontener karty pojedynczej oferty klienta w Panelu Handlowca

import React, { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Tab, Tabs } from '@mui/material';

import { useGetSalesCrmClientOffer, useGetTraderClient } from 'api';
import type { ISalesCrmOfferVersionListItem } from 'api/endpoints/useGetSalesCrmOfferVersions';
import TraderClientData from 'components/containers/TraderClientCardContainer/TraderClientData';
import TraderClientOfferData from 'components/containers/TraderClientOfferContainer/TraderClientOfferData';
import TraderClientOfferDataHeader from 'components/containers/TraderClientOfferContainer/TraderClientOfferDataHeader';
import TraderClientOfferDetails from 'components/containers/TraderClientOfferContainer/TraderClientOfferDetails';
import OfferCommentsCard from 'components/containers/TraderClientOfferShippingDetailsContainer/OfferCommentsCard';

import styles from 'theme/components/containers/TraderClientOfferContainer/TraderClientOfferContainer.module.scss';

const traderClientOfferTabsSx = {
  width: { xs: '100%', md: 'fit-content' },
  maxWidth: '100%',
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  overflow: 'hidden',
  border: '1px solid #e0e0e0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  '& .MuiButtonBase-root.MuiTab-root': {
    width: { xs: 'auto', md: 200 },
    flex: { xs: 1, md: 'none' },
    minWidth: 0,
    backgroundColor: '#ffffff',
    textTransform: 'none' as const
  },
  '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
    fontWeight: 600,
    backgroundColor: '#f3f4f7'
  }
};

interface ITraderClientOfferContainerProps {
  clientId: number;
  offerId: string;
  selectedVersionId?: number | null;
  onVersionChange?: (versionId: number | null) => void;
  versions?: ISalesCrmOfferVersionListItem[];
}

const TraderClientOfferContainer: React.FC<ITraderClientOfferContainerProps> = ({
  clientId,
  offerId,
  selectedVersionId = null,
  onVersionChange,
  versions = []
}) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);

  const {
    data: offer,
    isLoading: isOfferLoading,
    isError: isOfferError
  } = useGetSalesCrmClientOffer(clientId, offerId, selectedVersionId);

  const queryEnabled = clientId > 0 && !isOfferError && (isOfferLoading || Boolean(offer));

  const { data: client, isLoading: isClientLoading } = useGetTraderClient(clientId, {
    enabled: queryEnabled
  });

  const isClientDataLoading = isClientLoading || (isOfferLoading && !offer);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const renderDataTabContent = () => {
    if (isOfferLoading) {
      return (
        <div className={styles.clientDataWrapper}>
          <div>
            <TraderClientOfferDataHeader isLoading />
            <Grid container columnSpacing="48px">
              <Grid item xs={12} md={6}>
                <TraderClientData client={client ?? undefined} isLoading={isClientDataLoading} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TraderClientOfferData isLoading />
              </Grid>
            </Grid>
          </div>
          <TraderClientOfferDetails isLoading />
        </div>
      );
    }

    if (isOfferError) {
      return (
        <div className={styles.clientDataWrapper}>
          <p className={styles.placeholder}>{t('Nie udało się pobrać danych oferty.')}</p>
        </div>
      );
    }

    if (!offer) {
      return (
        <div className={styles.clientDataWrapper}>
          <p className={styles.placeholder}>{t('Nie znaleziono oferty.')}</p>
        </div>
      );
    }

    return (
      <div className={styles.clientDataWrapper}>
        <div>
          <TraderClientOfferDataHeader
            offer={offer}
            versions={versions}
            selectedVersionId={selectedVersionId}
            onVersionChange={onVersionChange}
            clientId={clientId}
            offerId={offerId}
          />
          <Grid container columnSpacing="48px">
            <Grid item xs={12} md={6}>
              <TraderClientData client={client ?? undefined} isLoading={isClientDataLoading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TraderClientOfferData
                offer={offer}
                client={client ?? null}
              />
            </Grid>
          </Grid>
        </div>
        <TraderClientOfferDetails offer={offer} clientId={clientId} offerId={offerId} />
      </div>
    );
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientOfferContainer'
      )}>
      <div className={styles.tabsCard}>
        <Box className={styles.tabsWrapper}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label={t('Oferta')}
            color="secondary"
            textColor="secondary"
            indicatorColor="secondary"
            sx={traderClientOfferTabsSx}>
            <Tab
              label={t('Dane').toUpperCase()}
              id="trader-offer-tab-0"
              aria-controls="trader-offer-tabpanel-0"
            />
            <Tab
              label={t('Komentarze').toUpperCase()}
              id="trader-offer-tab-1"
              aria-controls="trader-offer-tabpanel-1"
            />
          </Tabs>
        </Box>
        <Box
          role="tabpanel"
          id="trader-offer-tabpanel-0"
          aria-labelledby="trader-offer-tab-0"
          hidden={tabIndex !== 0}>
          {tabIndex === 0 && <div className={styles.tabPanelContent}>{renderDataTabContent()}</div>}
        </Box>
        <Box
          role="tabpanel"
          id="trader-offer-tabpanel-1"
          aria-labelledby="trader-offer-tab-1"
          hidden={tabIndex !== 1}>
          {tabIndex === 1 && (
            <div className={styles.tabPanelContent}>
              <OfferCommentsCard
                clientId={clientId}
                offerId={offerId}
                versionId={selectedVersionId}
              />
            </div>
          )}
        </Box>
      </div>
    </div>
  );
};

export default TraderClientOfferContainer;
